<!-- src/components/MenuViewer.vue -->
<!-- Reusable expanded menu viewer modal with cross-menu navigation -->
<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
    @click.self="close"
  >
    <!-- Close Button (X) - Top Right Corner -->
    <button
      @click="close"
      class="absolute top-4 right-4 z-50 p-2 text-white hover:text-sg transition-colors duration-200"
      aria-label="Close menu viewer"
    >
      <svg
        class="w-8 h-8"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M6 18L18 6M6 6l12 12"
        />
      </svg>
    </button>

    <!-- Left Arrow - Previous (always visible for cross-menu navigation) -->
    <button
      @click="navigatePrevious"
      class="absolute left-4 z-50 p-2 text-white hover:text-sg transition-colors duration-200"
      aria-label="Previous"
    >
      <svg
        class="w-10 h-10"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 19l-7-7 7-7"
        />
      </svg>
    </button>

    <!-- Right Arrow - Next (always visible for cross-menu navigation) -->
    <button
      @click="navigateNext"
      class="absolute right-4 z-50 p-2 text-white hover:text-sg transition-colors duration-200"
      aria-label="Next"
    >
      <svg
        class="w-10 h-10"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9 5l7 7-7 7"
        />
      </svg>
    </button>

    <!-- Top Info Bar - Menu title above the image -->
    <div class="absolute top-4 left-1/2 transform -translate-x-1/2 z-50">
      <div class="bg-black bg-opacity-70 px-4 py-2 rounded">
        <span class="text-white text-sm rl">
          {{ currentTitle }}
          <span v-if="currentMenuImages.length > 1">
            - Page {{ currentImageIndex + 1 }} / {{ currentMenuImages.length }}
          </span>
        </span>
      </div>
    </div>

    <!-- Menu Image Container - Fixed sizing to ensure full menu visibility -->
    <div
      class="relative flex items-center justify-center w-full h-full p-4 sm:p-8 md:p-12"
    >
      <img
        :src="currentImage"
        :alt="currentTitle + ' - Page ' + (currentImageIndex + 1)"
        class="max-w-full max-h-full object-contain shadow-lg"
        style="max-height: calc(100vh - 120px); max-width: calc(100vw - 120px)"
      />
    </div>

    <!-- Bottom Navigation - Menu navigation dots -->
    <div
      class="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex flex-col items-center space-y-2"
    >
      <!-- Menu Navigation Dots (for switching between menus) -->
      <div class="flex space-x-3">
        <button
          v-for="(menu, index) in allMenus"
          :key="menu.key"
          @click="switchToMenu(index)"
          class="flex flex-col items-center group"
          :aria-label="'View ' + menu.title"
        >
          <span
            :class="[
              'w-2 h-2 rounded-full transition-all duration-200',
              index === currentMenuIndex
                ? 'bg-sg scale-125'
                : 'bg-white bg-opacity-50 hover:bg-opacity-75',
            ]"
          />
          <span
            :class="[
              'text-xs mt-1 transition-colors duration-200',
              index === currentMenuIndex
                ? 'text-sg'
                : 'text-white text-opacity-60 group-hover:text-opacity-100',
            ]"
          >
            {{ menu.shortTitle }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
// src/components/MenuViewer.vue
export default {
  name: "MenuViewer",
  props: {
    // Whether the modal is open
    isOpen: {
      type: Boolean,
      default: false,
    },
    // Initial menu index to display (0-3 for brunch, dinner, dessert, drinks)
    initialMenuIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      // Current menu index (which menu type we're viewing)
      currentMenuIndex: 0,
      // Current image index within the current menu
      currentImageIndex: 0,
      // All available menus with their images and titles
      allMenus: [
        {
          key: "brunch",
          title: "Brunch Menu",
          shortTitle: "Brunch",
          images: ["/One-Squared-Menus-Dec-2025/One-Squared-Brunch-Menu.jpg"],
        },
        {
          key: "dinner",
          title: "Dinner Menu",
          shortTitle: "Dinner",
          images: ["/One-Squared-Menus-Dec-2025/One-squared-Dinner-Menu.jpeg"],
        },
        {
          key: "dessert",
          title: "Dessert Menu",
          shortTitle: "Dessert",
          images: ["/One-Squared-Menus-Dec-2025/One-Squared-Dessert-Menu.jpg"],
        },
        {
          key: "drinks",
          title: "Drinks Menu",
          shortTitle: "Drinks",
          images: [
            "/One-Squared-Menus-Dec-2025/Drinks-Menu-Page-1.jpg",
            "/One-Squared-Menus-Dec-2025/Drinks-Menu-Page-2.jpg",
          ],
        },
      ],
    };
  },
  computed: {
    // Get current menu object
    currentMenu() {
      return this.allMenus[this.currentMenuIndex] || this.allMenus[0];
    },
    // Get images for current menu
    currentMenuImages() {
      return this.currentMenu.images;
    },
    // Get current image URL
    currentImage() {
      return this.currentMenuImages[this.currentImageIndex] || "";
    },
    // Get current menu title
    currentTitle() {
      return this.currentMenu.title;
    },
  },
  watch: {
    // Reset to initial menu when modal opens
    isOpen(newVal) {
      if (newVal) {
        this.currentMenuIndex = this.initialMenuIndex;
        this.currentImageIndex = 0;
        // Prevent body scroll when modal is open
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    },
    // Update when initial menu index changes
    initialMenuIndex(newVal) {
      this.currentMenuIndex = newVal;
      this.currentImageIndex = 0;
    },
  },
  methods: {
    // Close the modal
    close() {
      this.$emit("close");
    },
    // Navigate to next image or next menu
    navigateNext() {
      // If there are more images in current menu, go to next image
      if (this.currentImageIndex < this.currentMenuImages.length - 1) {
        this.currentImageIndex++;
      } else {
        // Move to next menu (loop back to first if at end)
        this.currentMenuIndex =
          (this.currentMenuIndex + 1) % this.allMenus.length;
        this.currentImageIndex = 0;
      }
    },
    // Navigate to previous image or previous menu
    navigatePrevious() {
      // If there are previous images in current menu, go to previous image
      if (this.currentImageIndex > 0) {
        this.currentImageIndex--;
      } else {
        // Move to previous menu (loop to last if at beginning)
        this.currentMenuIndex =
          this.currentMenuIndex === 0
            ? this.allMenus.length - 1
            : this.currentMenuIndex - 1;
        // Start at last image of that menu
        this.currentImageIndex =
          this.allMenus[this.currentMenuIndex].images.length - 1;
      }
    },
    // Switch to a specific menu
    switchToMenu(index) {
      this.currentMenuIndex = index;
      this.currentImageIndex = 0;
    },
    // Handle keyboard navigation
    handleKeydown(event) {
      if (!this.isOpen) return;

      switch (event.key) {
        case "Escape":
          this.close();
          break;
        case "ArrowLeft":
          this.navigatePrevious();
          break;
        case "ArrowRight":
          this.navigateNext();
          break;
      }
    },
  },
  mounted() {
    // Add keyboard listener for navigation
    document.addEventListener("keydown", this.handleKeydown);
  },
  beforeUnmount() {
    // Clean up keyboard listener
    document.removeEventListener("keydown", this.handleKeydown);
    // Reset body scroll
    document.body.style.overflow = "";
  },
};
</script>

<style scoped>
/* Smooth fade animation for modal */
.fixed {
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}
</style>
